import type { BlogPost } from "@/data/blogPosts";

const STOP_WORDS = new Set([
  "about", "after", "against", "also", "analysis", "and", "are", "article", "content", "current",
  "evidence", "from", "guide", "into", "more", "over", "practical", "published", "that", "the",
  "their", "these", "this", "through", "what", "when", "with", "your",
]);

function keywordSet(post: BlogPost): Set<string> {
  return new Set(
    `${post.title} ${post.excerpt}`
      .toLowerCase()
      .match(/[a-z0-9]{4,}/g)
      ?.filter((word) => !STOP_WORDS.has(word)) ?? [],
  );
}

function sharedKeywordCount(left: Set<string>, right: Set<string>): number {
  let count = 0;
  for (const word of Array.from(left)) {
    if (right.has(word)) count += 1;
  }
  return count;
}

/**
 * Resolves a dynamic end-of-article reading path without exposing unpublished
 * records or creating self-links. Curated relatedSlugs always retain their
 * editorial order; if fewer than the requested number are available, the
 * resolver fills remaining places with current, same-topic articles and then
 * the closest title/excerpt matches.
 */
export function resolveDynamicRelatedPosts(
  currentPost: BlogPost,
  allPosts: BlogPost[],
  limit = 4,
): BlogPost[] {
  const bySlug = new Map(allPosts.map((post) => [post.slug, post]));
  const selected: BlogPost[] = [];
  const selectedSlugs = new Set([currentPost.slug]);

  const add = (candidate: BlogPost | undefined) => {
    if (!candidate || selectedSlugs.has(candidate.slug) || selected.length >= limit) return;
    selected.push(candidate);
    selectedSlugs.add(candidate.slug);
  };

  // Preserve intentional editorial recommendations first.
  currentPost.relatedSlugs.forEach((slug) => add(bySlug.get(slug)));
  if (selected.length >= limit) return selected;

  const currentKeywords = keywordSet(currentPost);
  const rankedFallbacks = allPosts
    .filter((candidate) => !selectedSlugs.has(candidate.slug))
    .map((candidate) => {
      const sharedTags = candidate.tags.filter((tag) => currentPost.tags.includes(tag)).length;
      const sharedKeywords = sharedKeywordCount(currentKeywords, keywordSet(candidate));
      const publishedAt = new Date(candidate.date).getTime();

      return { candidate, sharedTags, sharedKeywords, publishedAt };
    })
    .filter(({ sharedTags, sharedKeywords }) => sharedTags > 0 || sharedKeywords > 0)
    .sort((left, right) =>
      right.sharedTags - left.sharedTags ||
      right.sharedKeywords - left.sharedKeywords ||
      right.publishedAt - left.publishedAt ||
      left.candidate.slug.localeCompare(right.candidate.slug),
    );

  rankedFallbacks.forEach(({ candidate }) => add(candidate));
  return selected;
}
